import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlarmIcon } from "~/components/icons/alarm-icon";
import { useCountdown } from "~/hooks/use-countdown.hook";
import { useCart } from "./cart.context";

const RESERVATION_DURATION_MS = 15 * 60 * 1000;
const RESERVATION_FALLBACK_SECONDS = RESERVATION_DURATION_MS / 1000;
const RESERVATION_STORAGE_KEY = "dransay.cart-reservation";
const IS_SERVER = typeof window === "undefined";

interface StoredReservation {
	readonly deadline: number;
	readonly itemIds: readonly string[];
}

function readReservation(): StoredReservation | null {
	if (IS_SERVER) {
		return null;
	}
	try {
		const raw = window.sessionStorage.getItem(RESERVATION_STORAGE_KEY);
		if (raw === null) {
			return null;
		}
		const parsed = JSON.parse(raw) as Partial<StoredReservation>;
		const hasValidDeadline =
			typeof parsed.deadline === "number" && Number.isFinite(parsed.deadline);
		const hasValidItemIds =
			Array.isArray(parsed.itemIds) &&
			parsed.itemIds.every((id) => typeof id === "string");
		return hasValidDeadline && hasValidItemIds
			? {
					deadline: parsed.deadline as number,
					itemIds: parsed.itemIds as string[],
				}
			: null;
	} catch {
		return null;
	}
}

function persistReservation(reservation: StoredReservation): void {
	if (IS_SERVER) {
		return;
	}
	try {
		window.sessionStorage.setItem(
			RESERVATION_STORAGE_KEY,
			JSON.stringify(reservation),
		);
	} catch {
		// sessionStorage may be unavailable (disabled, quota, private mode) —
		// fall back to the in-memory deadline for the current render.
	}
}

function hasUnseenItem(
	currentIds: readonly string[],
	knownIds: readonly string[],
): boolean {
	return currentIds.some((id) => !knownIds.includes(id));
}

function formatCountdown(totalSeconds: number): string {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	const paddedMinutes = minutes.toString().padStart(2, "0");
	const paddedSeconds = seconds.toString().padStart(2, "0");
	return `${paddedMinutes}:${paddedSeconds}`;
}

export function CartReservationTimer() {
	const { t } = useTranslation("cart");
	const { items } = useCart();
	const itemIds = useMemo(() => items.map((item) => item.id), [items]);
	const signature = itemIds.join("|");
	const [deadline, setDeadline] = useState<number | null>(null);
	const knownItemIds = useRef<readonly string[]>(itemIds);
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only resume/start of the reservation
	useEffect(() => {
		const stored = readReservation();
		const shouldRestart =
			stored === null || hasUnseenItem(itemIds, stored.itemIds);
		if (!shouldRestart && stored !== null) {
			persistReservation({ deadline: stored.deadline, itemIds });
			setDeadline(stored.deadline);
			return;
		}
		const nextDeadline = Date.now() + RESERVATION_DURATION_MS;
		persistReservation({ deadline: nextDeadline, itemIds });
		setDeadline(nextDeadline);
	}, []);
	// biome-ignore lint/correctness/useExhaustiveDependencies: restart only when a new item id appears (signature drives the run)
	useEffect(() => {
		if (hasUnseenItem(itemIds, knownItemIds.current)) {
			const nextDeadline = Date.now() + RESERVATION_DURATION_MS;
			persistReservation({ deadline: nextDeadline, itemIds });
			setDeadline(nextDeadline);
		}
		knownItemIds.current = itemIds;
	}, [signature]);
	const secondsLeft = useCountdown(deadline);
	const displaySeconds = secondsLeft ?? RESERVATION_FALLBACK_SECONDS;
	const copyKey =
		displaySeconds === 0 ? "reservation.expiredCopy" : "reservation.copy";
	return (
		<div
			className="flex w-full items-center gap-3 px-4 pb-4 md:px-6"
			data-testid="cart-reservation-timer"
		>
			<div className="flex h-10 shrink-0 items-center justify-center gap-1 rounded-lg bg-ansay-900 p-2">
				<AlarmIcon className="size-5 text-ansay-light" aria-hidden="true" />
				<span
					className="text-body text-right text-white tabular-nums"
					data-testid="cart-reservation-timer-value"
				>
					{formatCountdown(displaySeconds)}
				</span>
			</div>
			<p className="flex-1 text-body-sm text-gray-700">{t(copyKey)}</p>
		</div>
	);
}
